/**
 * The scoring rules of the Hard-Wired diagnostic, as pure functions.
 *
 * This is a direct mirror of SPEC section 4. Everything here is driven by the
 * `DiagnosticContent` payload the backend serves: no question text, chip
 * score, reflection or level threshold is hardcoded, so editorial changes ship
 * from the backend without touching the frontend.
 */

import type {
  DiagnosticAnswer,
  DiagnosticContent,
  DiagnosticHeuristic,
  DiagnosticQuestion,
  ScoreResponse,
} from "@/lib/api";

/** An answer score: 0 = hard-wired, 1 = mostly hard-wired, 2 = rewiring, 3 = AI-first. */
export type AnswerScore = 0 | 1 | 2 | 3;

/** The three Hard-Wired tracks, by id. */
export type TrackId = 0 | 1 | 2;

/** Where an answer's score came from. */
export type AnswerSource = DiagnosticAnswer["source"];

/** The scored half of an answer: what `/score` returns, and what chips stand in with. */
export interface ScoredParts {
  score: AnswerScore;
  evidence: string;
  reflection: string;
  source: AnswerSource;
}

/** Anything with the shape the aggregate helpers need. */
export interface ScoredAnswer {
  track: number;
  score: number;
}

function clampScore(value: number): AnswerScore {
  const rounded = Math.round(value);
  if (rounded <= 0) {
    return 0;
  }
  if (rounded >= 3) {
    return 3;
  }
  return rounded as AnswerScore;
}

/** JS `Math.round` already rounds halves up for non-negative numbers. */
function roundHalfUp(value: number): number {
  return Math.round(value);
}

/** Narrow a track id coming off the content payload. */
export function asTrackId(track: number): TrackId {
  return (track === 1 || track === 2 ? track : 0) as TrackId;
}

/**
 * Score a chip-only answer.
 *
 * Single-select: the chip's own score and reflection.
 * Multi-select: `p` positives and `n` negatives drive both the score and which
 * of the three multi-select reflections is used. An empty selection is a real
 * answer there — it scores 0 and earns the "nothing ticked" reflection.
 */
export function scoreChips(
  question: DiagnosticQuestion,
  chipLabels: string[],
  content: DiagnosticContent,
): { score: AnswerScore; reflection: string } {
  const picked = question.options.filter((option) => chipLabels.includes(option.label));

  if (question.multi) {
    const positives = picked.filter((option) => option.score === 1).length;
    const negatives = picked.filter((option) => option.score === -1).length;

    const score: AnswerScore = clampScore(Math.min(3, positives));

    const reflections = content.multiSelectRule.reflections;
    let reflection = reflections.noneTicked;
    if (positives >= 2) {
      reflection = reflections.twoOrMorePositive;
    } else if (negatives > 0) {
      reflection = reflections.onlyNegative;
    }

    return { score, reflection };
  }

  const chosen = picked[0];
  if (!chosen) {
    return { score: 0, reflection: "" };
  }
  return { score: clampScore(chosen.score), reflection: chosen.reflection ?? "" };
}

/**
 * The keyword fallback used when there is no chip and no model.
 *
 * Every positive and negative entry counts at most once, matched as a
 * substring of the lowercased answer.
 */
export function scoreHeuristic(text: string, heuristic: DiagnosticHeuristic): AnswerScore {
  const haystack = text.toLowerCase();
  const positives = heuristic.positive.filter((word) => haystack.includes(word)).length;
  const negatives = heuristic.negative.filter((word) => haystack.includes(word)).length;
  return clampScore(1 + positives - negatives);
}

const SOURCES: readonly string[] = ["chip", "model", "heuristic"];

/** Is this the `/score` payload, rather than an error or a half-written body? */
function isScoreResponse(value: unknown): value is ScoreResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Partial<ScoreResponse>;
  return (
    typeof candidate.score === "number" &&
    Number.isFinite(candidate.score) &&
    typeof candidate.evidence === "string" &&
    typeof candidate.reflection === "string" &&
    typeof candidate.source === "string" &&
    SOURCES.includes(candidate.source)
  );
}

/**
 * Decide what a sent answer is worth: the model's reading, or the local one.
 *
 * `remote` is whatever came back from `/score` — the payload, a thrown error,
 * or nothing at all if the request timed out. Anything that is not a usable
 * response leaves `local` (the chip score) standing, so a slow or broken
 * backend costs the visitor a sharper reflection and never the question.
 *
 * Empty strings from the model are treated as missing: the chip's evidence and
 * reflection are better than a blank line in the chat.
 */
export function resolveScore(local: ScoredParts, remote: unknown): ScoredParts {
  if (!isScoreResponse(remote)) {
    return local;
  }
  return {
    score: clampScore(remote.score),
    evidence: remote.evidence || local.evidence,
    reflection: remote.reflection || local.reflection,
    source: remote.source,
  };
}

/**
 * Per-track score out of 100, in the order the content lists the tracks.
 * A track with no answers scores 0.
 */
export function trackScores(answers: ScoredAnswer[], content: DiagnosticContent): number[] {
  return content.tracks.map((track) => {
    const forTrack = answers.filter((answer) => answer.track === track.id);
    if (forTrack.length === 0) {
      return 0;
    }
    const total = forTrack.reduce((sum, answer) => sum + answer.score, 0);
    return roundHalfUp((total / (forTrack.length * 3)) * 100);
  });
}

/** The index out of 100: the mean of the track scores. */
export function indexFromTracks(tracks: number[]): number {
  if (tracks.length === 0) {
    return 0;
  }
  const total = tracks.reduce((sum, value) => sum + value, 0);
  return roundHalfUp(total / tracks.length);
}

/** The level label for an index, derived from the content's level bands. */
export function levelFor(index: number, content: DiagnosticContent): string {
  const bands = [...content.levels].sort((a, b) => b.min - a.min);
  const match = bands.find((band) => index >= band.min);
  return match?.label ?? "";
}

/** The index of the lowest-scoring track. Ties resolve to the first track. */
export function weakestTrack(tracks: number[]): number {
  let weakest = 0;
  for (let i = 1; i < tracks.length; i += 1) {
    const current = tracks[i];
    const best = tracks[weakest];
    if (current !== undefined && best !== undefined && current < best) {
      weakest = i;
    }
  }
  return weakest;
}

/** The per-answer level label ("Hard-wired" .. "AI-first"). */
export function answerLevel(score: number, content: DiagnosticContent): string {
  return content.answerLevels[score] ?? "";
}

/**
 * The telling phrase quoted back to the user: the first sentence, truncated.
 *
 * Mirrors the backend's `first_sentence`: the first *non-empty* chunk before a
 * ".", "!" or "?", so a leading separator ("... Then we wait.") does not yield
 * an empty phrase.
 */
export function firstSentence(text: string, max = 90): string {
  for (const chunk of text.split(/[.!?]/)) {
    const trimmed = chunk.trim();
    if (trimmed.length > 0) {
      return trimmed.slice(0, max).trimEnd();
    }
  }
  return "";
}
